import React from 'react';
import { useAppState } from 'providers/AppState';
import appConfig from '../app.config';
import { getPartner } from '../helpers/partner';
import { Card, Typography, Descriptions, Tag } from 'antd';

const { Title, Paragraph, Text } = Typography;

export default function ConfigDebug() {
  const { partner } = useAppState();
  const partnerConfig = appConfig[partner];
  const hostname = window.location.hostname;

  let environment = 'PRODUCTION';
  if (hostname.startsWith('my-customer-space-preproduction')) {
    environment = 'PREPRODUCTION';
  } else if (
    hostname.startsWith('my-customer-space-staging') ||
    hostname.includes('localhost')
  ) {
    environment = 'STAGING / LOCAL';
  }

  const getEnvironmentColor = () => {
    if (environment === 'PRODUCTION') return 'red';
    if (environment === 'PREPRODUCTION') return 'orange';
    return 'blue';
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <Card>
        <Title level={2}>🔍 Configuration Debug</Title>
        <Paragraph>
          <Text type="warning">
            ⚠️ This page is for testing only. Remove before production!
          </Text>
        </Paragraph>

        <Descriptions bordered column={1} style={{ marginTop: '20px' }}>
          <Descriptions.Item label="Current Hostname">
            <Text code>{hostname}</Text>
          </Descriptions.Item>

          <Descriptions.Item label="Detected Environment">
            <Tag color={getEnvironmentColor()} style={{ fontSize: '14px' }}>
              {environment}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Active Partner">
            <Tag
              color="purple"
              style={{ fontSize: '16px', fontWeight: 'bold' }}
            >
              {partner}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Partner Title">
            {partnerConfig?.title || 'N/A'}
          </Descriptions.Item>

          <Descriptions.Item label="Google Analytics ID">
            <Text code style={{ fontSize: '16px', color: '#1890ff' }}>
              {partnerConfig?.ga || 'NOT SET'}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Google Tag Manager ID">
            <Text code style={{ fontSize: '16px', color: '#52c41a' }}>
              {partnerConfig?.gtm || 'NOT SET'}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Default Language">
            {partnerConfig?.defaultLang || 'N/A'}
          </Descriptions.Item>

          <Descriptions.Item label="Available Languages">
            {partnerConfig?.langs?.join(', ') || 'N/A'}
          </Descriptions.Item>

          <Descriptions.Item label="Company">
            {partnerConfig?.company || 'N/A'}
          </Descriptions.Item>
        </Descriptions>

        <Card
          title="📋 All Available Partners"
          style={{ marginTop: '30px' }}
          type="inner"
        >
          {Object.keys(appConfig)
            .filter(
              key =>
                key !== 'customer' &&
                key !== 'caption' &&
                key !== 'description',
            )
            .map(partnerKey => (
              <Card
                key={partnerKey}
                type="inner"
                title={
                  <span>
                    {partnerKey === partner ? '✅ ' : ''}
                    {partnerKey} - {appConfig[partnerKey]?.title}
                  </span>
                }
                style={{ marginBottom: '10px' }}
              >
                <p>
                  <strong>GA:</strong>{' '}
                  <Text code>{appConfig[partnerKey]?.ga || 'NOT SET'}</Text>
                </p>
                <p>
                  <strong>GTM:</strong>{' '}
                  <Text code>{appConfig[partnerKey]?.gtm || 'NOT SET'}</Text>
                </p>
              </Card>
            ))}
        </Card>

        <Card
          title="🔧 Testing Checklist"
          style={{ marginTop: '30px' }}
          type="inner"
        >
          <ul>
            <li>
              ✅ Check that the <strong>Active Partner</strong> matches your
              expectation
            </li>
            <li>
              ✅ Verify the <strong>Environment</strong> is correctly detected
            </li>
            <li>
              ✅ Confirm the <strong>GA and GTM IDs</strong> are correct for
              this partner/environment
            </li>
            <li>
              ✅ Open DevTools → Network tab and verify requests go to these IDs
            </li>
            <li>
              ✅ Use{' '}
              <a
                href="https://tagassistant.google.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Tag Assistant
              </a>{' '}
              to verify GTM is firing correctly
            </li>
            <li>
              ✅ Check Google Analytics Real-Time reports to see your test
              traffic
            </li>
          </ul>
        </Card>
      </Card>
    </div>
  );
}
